/**
 * 🛑 Caution: This is a legacy map component ported from @gravitate/js-excalibrr.
 *
 * While the code may seem outdated or in need of cleanup, please avoid refactoring it.
 * A fragile monkey patch was added to the Google Maps Directions API to cache responses
 * and minimize API usage costs.
 *
 * This component is slated for replacement with a more modern and stable solution.
 * Until then, any structural changes are highly likely to break map functionality.
 * Proceed with extreme caution.
 */

import { memo, Component } from 'react'
import { DirectionsRenderer, GoogleMap, withGoogleMap, withScriptjs } from 'react-google-maps'
import { buildDirectionsRequest, fetchDirections, fixMapsApiResponse, MAPS_KEY } from './util'

export type Stop = {
  lat: number
  lng: number
}

type RouteMapProps = {
  stops: Stop[]
  options?: any
}

export const RouteMap = memo(function RouteMap({ stops, options }: RouteMapProps) {
  const MapLoader = withScriptjs(MapWithRoute)
  return (
    <MapLoader
      // @ts-ignore - withScriptjs doesn't really tell us anything about the dynamic component, so we have to ignore this
      options={options}
      // This should stay the original URL as it is used to load the Google Maps API under the hood
      googleMapURL={`https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}`}
      loadingElement={<div style={{ height: `100%` }} />}
      stops={stops}
    />
  )
})

class MapWithRoute extends Component<
  RouteMapProps,
  {
    directions: {
      routes: any[] // We might want to type this from the google response
    }
  }
> {
  state = {
    directions: { routes: [] },
  }

  componentDidMount() {
    const { stops } = { ...this.props }
    const stopCopy = [...stops]
    const origin = stopCopy.shift()
    const destination = stopCopy.pop()
    const waypoints = stopCopy.map((stop) => {
      return { location: new google.maps.LatLng(stop.lat, stop.lng) }
    })

    const { request, params } = buildDirectionsRequest(origin, destination, waypoints)

    fetchDirections(request, params).then((directions) => {
      fixMapsApiResponse(request, directions)
      this.setState({ directions })
    })
  }

  render() {
    const GoogleMapExample = withGoogleMap<{
      options: any
    }>((props: { options: any }) => (
      <>
        {/* @ts-ignore - withGoogleMap says GoogleMap doesn't have children, I say I know better */}
        <GoogleMap
          options={{
            fullscreenControl: false,
            disableDefaultUI: true,
            ...props.options,
          }}
        >
          <DirectionsRenderer directions={this.state.directions} />
        </GoogleMap>
      </>
    ))

    return (
      <GoogleMapExample
        containerElement={<div style={{ height: `100%` }} />}
        mapElement={<div style={{ height: `100%` }} />}
        options={this.props.options}
      />
    )
  }
}
